import { Component, forwardRef, inject, input } from '@angular/core';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { EmailValidationService } from '@services/email-validation.service';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UserFilterDto, UserItemDto, UserService } from '@artemis-software/wr-api';
import { CurrentUserService } from '@services/current-user.service';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs';

export interface RecipientSuggestion { email: string; display: string }

@Component({
  selector: 'wr-select-recipient',
  standalone: false,
  templateUrl: './select-recipient.component.html',
  styleUrl: './select-recipient.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(()=> SelectRecipientComponent),
      multi: true,
    },
  ],
})
export class SelectRecipientComponent extends AbstractFormControl<string[]>{

  readonly disabled = input(false);
  readonly inputName = input('email');
  readonly autocomplete = input('off');
  readonly presetSuggestions = input<RecipientSuggestion[]>([]);

  private readonly emailValidationService = inject(EmailValidationService);
  private readonly userService = inject(UserService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly tenantId = this.currentUserService.currentUser()?.tenant?.id;


  readonly inputCtrl = new FormControl<string>('', { nonNullable: true });

  readonly suggestions$: Observable<RecipientSuggestion[]> = combineLatest([
    this.inputCtrl.valueChanges.pipe(
      startWith(this.inputCtrl.value),
      map(v => (v ?? '').trim()),
      debounceTime(350),
      distinctUntilChanged(),
    ),
    this.value$,
  ]).pipe(
    switchMap(([term]) => {
      const presets = this.availablePresets(term);
      if (term.length < 2 || !this.tenantId) {
        return of(presets);
      }

      const filter: UserFilterDto = { tenantId: this.tenantId, fullName: term } as any;

      return this.userService.findAll(filter).pipe(
        map((users: UserItemDto[]) => {
          const presetEmails = new Set(presets.map(preset => preset.email));
          const userSuggestions = (users ?? [])
            .filter(u => !!u.email)
            .filter(u => !(this.recipients ?? []).includes(u.email))
            .filter(u => !presetEmails.has(u.email))
            .slice(0, 20)
            .map(u => ({
              email: u.email,
              display: `${u.fullName} <${u.email}>`,
            }));
          return [...presets, ...userSuggestions];
        }),
        catchError(() => of(presets)),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  get recipients(): string[] {
    return this.value ?? [];
  }

  private availablePresets(term: string): RecipientSuggestion[] {
    const lowerTerm = term.toLowerCase();
    return this.presetSuggestions()
      .filter(suggestion => !(this.recipients ?? []).includes(suggestion.email))
      .filter(suggestion => !lowerTerm || suggestion.display.toLowerCase().includes(lowerTerm));
  }

  onSuggestionSelected(email: string) {
    this.addEmail(email);
  }

  private addEmail(raw: string) {
    const emailAddress = (raw ?? '').trim();
    if (!emailAddress) return;
    if (!this.emailValidationService.isValidEmail(emailAddress)) return;

    const currentRecipients = this.recipients;
    if (!currentRecipients.includes(emailAddress)) {
      this.value = [...currentRecipients, emailAddress];
    }

    this.inputCtrl.setValue('');
  }

  onTagAdd(tagEvent: { value: string }): void {
    this.addEmail(tagEvent.value);
    this.inputCtrl.setValue('');
  }

  onTagRemove(tagEvent: { text: string }): void {
    const emailToRemove = tagEvent.text;
    const currentRecipients = this.recipients;

    this.value = currentRecipients.filter(
      existingEmail => existingEmail !== emailToRemove
    );
  }
}
